import { type AIModelChoices, type AllowedMaterialSpecInfoAiModel, type SiteMaterialDetail } from 'api';
import { useTranslation } from 'i18n';
import { useMemo, useState } from 'react';
import {
  Box,
  Fade,
  LinearProgress,
  MediaDialog,
  type PhotoEvidenceItem,
  PhotoEvidenceList,
  PhotoFilePicker,
  Typography,
  useBreakpoint,
} from 'ui';

import { CameraDialog, type PreviewGalleryImage, useManagePreviews } from '../../../camera-guidance';
import { getPhotoVariant, useBaleAnalysis } from '../../../grade';
import { useGetEvidenceList } from '../../hooks/useGetEvidenceList/useGetEvidenceList';

interface BaleEvidenceGalleryProps {
  onToggleCamera: () => void;
  isCameraOpen: boolean;
  filePickerRef: React.RefObject<HTMLInputElement | null>;
  siteMaterial?: SiteMaterialDetail;
  model?: AllowedMaterialSpecInfoAiModel;
  load?: { id: number };
  onUpload?: (model: string, photo: File) => void;
}

export const BaleEvidenceGallery = ({
  onToggleCamera,
  isCameraOpen,
  filePickerRef,
  siteMaterial,
  model,
  load,
  onUpload,
}: BaleEvidenceGalleryProps) => {
  const { t } = useTranslation('features');
  if (!siteMaterial && !load) {
    throw new Error('Site material or load is required');
  }
  const { isMobile } = useBreakpoint();
  const [selectedImage, setSelectedImage] = useState<string>('');
  const { data } = useGetEvidenceList({
    mode: load ? 'load' : 'site-material',
    parentId: load?.id ?? siteMaterial?.id ?? 0,
  });
  const { addBalePhoto, bales, removeBalePhoto } = useBaleAnalysis();

  const allPhotos = useMemo(
    () =>
      data?.map(({ file, preview, model_results }) => ({
        disabled: !model_results,
        isLoading: false,
        src: (preview || file) ?? '',
        variant: getPhotoVariant(model_results ?? undefined),
      })),
    [data]
  );

  const analyzingBales = useMemo(
    () => bales.filter((bale) => bale.isLoading || (bale.uploadProgress !== undefined && bale.uploadProgress < 100)),
    [bales]
  );

  const uploadProgress = useMemo(
    () =>
      analyzingBales.length > 0
        ? analyzingBales.reduce((acc, bale) => acc + (bale.uploadProgress ?? 0), 0) / analyzingBales.length
        : 0,
    [analyzingBales]
  );

  const carouselMedia = useMemo(() => data?.map((result) => String(result.preview || result.file)) ?? [], [data]);

  const { previewImages, addImage, updateOnlineImage, resetSelectedImages, handleDeletePreviewImage } =
    useManagePreviews({
      mode: load ? 'load' : 'site-material',
      parentId: load?.id ?? siteMaterial?.id ?? 0,
    });

  const handleCapture = async (photo: File) => {
    const addedImage = await addImage({
      photo,
    });

    const result = await addBalePhoto({ model: model ? String(model) : undefined, photo });
    const resultModel = result?.model_results?.model_name as AIModelChoices | undefined;

    if (result && 'model_results' in result && result.model_results) {
      await updateOnlineImage({
        accepted: !(result.model_results.outputs?.[0].image_rejected ?? true),
        aiModel: (model as AIModelChoices) ?? undefined,
        aluminiumComposition: result.aluminium_composition ?? undefined,
        annotations: [...(result.model_results.coco_prediction ?? [])],
        evaluatedSpecs: result.evaluated_specs ?? undefined,
        imageRejectionReasons: result.model_results.outputs?.[0]?.image_rejection_reasons ?? [],
        imageWarningReasons: result.model_results.outputs?.[0]?.image_warning_reasons ?? [],
        petComposition: result.pet_composition ?? undefined,
        previewImage: addedImage,
        targetWeightPercentage: result.target_weight_percentage ?? undefined,
        url: (typeof result.image === 'string' ? result.image : result.image.url) || addedImage.src,
      });
    }

    onUpload?.(String(model || resultModel), photo);
  };

  const handleSelect = (photo: PhotoEvidenceItem) => {
    setSelectedImage(photo.src);
  };

  const handleCompletePreviewSession = () => {
    resetSelectedImages();
    onToggleCamera();
  };

  const deletePreviewImage = (image: PreviewGalleryImage) => {
    if (!data?.length) {
      return;
    }

    handleDeletePreviewImage({
      evidence: data,
      image,
      removeEvidenceFile: removeBalePhoto,
    });
  };

  return (
    <>
      {data?.length === 0 ? (
        <Typography>{t('backlog.evidence.baleEvidenceGallery.noEvidenceFound')}</Typography>
      ) : (
        <PhotoEvidenceList
          slotProps={{
            imageList: {
              cols: isMobile ? 3 : 5,
              gap: 16,
            },
          }}
          evidencePhotos={allPhotos ?? []}
          handleClick={handleSelect}
          openOnClick
        />
      )}
      <Fade in={analyzingBales.length > 0} timeout={300}>
        <Box
          sx={{
            marginTop: 4,
          }}
        >
          <LinearProgress value={uploadProgress} />
          <Typography
            variant="body2"
            color="secondary"
            sx={{
              textAlign: 'center',
            }}
          >
            {t(
              analyzingBales.length === 1
                ? 'backlog.evidence.baleEvidenceGallery.analysingSinglePhoto'
                : 'backlog.evidence.baleEvidenceGallery.analysingMultiplePhotos',
              {
                values: {
                  count: analyzingBales.length,
                },
              }
            )}
          </Typography>
        </Box>
      </Fade>
      <CameraDialog
        isCameraOpen={isCameraOpen}
        toggle={onToggleCamera}
        cameraProps={{
          aiModel: model ?? 'alu',
          handleCapture,
          handleClose: onToggleCamera,
          handleDeletePreviewImage: deletePreviewImage,
          handleOnComplete: handleCompletePreviewSession,
          previewImages,
        }}
      />
      <PhotoFilePicker
        ref={filePickerRef}
        onSelectFiles={(photos) => photos.forEach((photo) => handleCapture(photo))}
        hide={true}
        multiple={true}
      />
      <MediaDialog
        open={Boolean(selectedImage)}
        selectedMedium={selectedImage}
        onClose={() => setSelectedImage('')}
        media={carouselMedia}
        handleNavigate={setSelectedImage}
        imageCount={carouselMedia.length}
      />
    </>
  );
};
